import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FileText, FileCheck, BarChart2, Receipt, Shield, File,
  Upload, Download, Trash2, Search, X, ChevronDown, ChevronRight,
  Plus, PenLine, Clock, CheckCircle2, XCircle, LayoutTemplate, Eye,
  Pencil, FileSpreadsheet, Presentation, FilePlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useModules, hasModule } from '@/lib/modules';
import { editorAPI, canOpenInEditor, canEditInEditor } from '@/lib/editorAPI';
import { documentsAPI, contactsAPI, esignAPI } from '@/lib/api';
import PrepareDocumentModal from './PrepareDocumentModal';
import SubmissionViewerModal from './SubmissionViewerModal';
import DocumentPreviewModal from './DocumentPreviewModal';
import { useProject } from '@/lib/ProjectContext';
import { useT } from '@/lib/LanguageContext';
import { toast } from 'sonner';
import { safeFormat } from '@/lib/utils';

// ── Constants ─────────────────────────────────────────────────────────────────

const DOC_TYPES = [
  { id: 'contract',  label: 'Contract',  icon: FileCheck,  color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { id: 'proposal',  label: 'Proposal',  icon: FileText,   color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { id: 'report',    label: 'Report',    icon: BarChart2,  color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'invoice',   label: 'Invoice',   icon: Receipt,    color: 'bg-green-100 text-green-700 border-green-300' },
  { id: 'nda',       label: 'NDA',       icon: Shield,     color: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'other',     label: 'Other',     icon: File,       color: 'bg-gray-100 text-gray-600 border-gray-300' },
];

const docTypeMap = Object.fromEntries(DOC_TYPES.map((d) => [d.id, d]));

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const PROJECT_COLORS = [
  '#A62B1F', '#A62B1F', '#3B82F6', '#10B981', '#F59E0B',
  '#EF4444', '#06B6D4', '#A855F7', '#F97316', '#84CC16',
];

const projectColor = (project, idx) => project?.color || PROJECT_COLORS[idx % PROJECT_COLORS.length];

// ── DocTypeBadge ──────────────────────────────────────────────────────────────

const DocTypeBadge = ({ type }) => {
  const info = docTypeMap[type] || docTypeMap.other;
  const Icon = info.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${info.color}`}>
      <Icon className="w-3 h-3" />
      {info.label}
    </span>
  );
};

// ── ProjectChip ───────────────────────────────────────────────────────────────

const ProjectChip = ({ project, idx }) => (
  <span
    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
    style={{
      backgroundColor: `${projectColor(project, idx)}22`,
      color: projectColor(project, idx),
      border: `1px solid ${projectColor(project, idx)}44`,
    }}
  >
    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: projectColor(project, idx) }} />
    {project?.name || 'Unknown'}
  </span>
);

// ── ESignBadge — shows a document's signature status inline in the list ──────

const ESIGN_STATUS = {
  pending:             { label: 'Awaiting Signature', icon: Clock,         color: 'bg-amber-50 text-amber-700 border-amber-300' },
  partially_signed:    { label: 'In Progress',        icon: Clock,         color: 'bg-amber-50 text-amber-700 border-amber-300' },
  awaiting_execution:  { label: 'Awaiting Your Part', icon: CheckCircle2,  color: 'bg-blue-50 text-blue-700 border-blue-300' },
  executed:            { label: 'Fully Executed',     icon: CheckCircle2,  color: 'bg-green-50 text-green-700 border-green-300' },
  declined:            { label: 'Declined',           icon: XCircle,       color: 'bg-red-50 text-red-600 border-red-300' },
};

const ESignBadge = ({ status }) => {
  const info = ESIGN_STATUS[status];
  if (!info) return null;
  const Icon = info.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${info.color}`}>
      <Icon className="w-3 h-3" />
      {info.label}
    </span>
  );
};

// ── UploadDialog ──────────────────────────────────────────────────────────────

function UploadDialog({ open, onClose, projects, contacts, onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('other');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [contactId, setContactId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSignable, setIsSignable] = useState(true);
  const [uploading, setUploading] = useState(false);

  const contactMap = useMemo(() =>
    Object.fromEntries(contacts.map((c) => [c._id, c])), [contacts]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const toggleProject = (id) =>
    setSelectedProjectIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const reset = () => {
    setFile(null);
    setTitle('');
    setDocType('other');
    setSelectedProjectIds([]);
    setContactId('');
    setNotes('');
    setIsSignable(true);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) {
      toast.error('Please select a file and enter a title.');
      return;
    }
    setUploading(true);
    try {
      const contact = contactMap[contactId];
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('doc_type', docType);
      fd.append('project_ids', selectedProjectIds.join(','));
      if (contactId) {
        fd.append('contact_id', contactId);
        fd.append('contact_name', contact?.full_name || '');
        fd.append('account_name', contact?.company || '');
      }
      if (notes.trim()) fd.append('notes', notes.trim());
      fd.append('is_signable', isSignable ? 'true' : 'false');

      const res = await documentsAPI.upload(fd);
      toast.success('Document uploaded');
      onUploaded(res.data);
      onClose();
      reset();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); reset(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>Upload a file and tag it with projects and a contact.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* File picker */}
          <div>
            <Label className="mb-1.5 block">File</Label>
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-[#A62B1F] transition-colors"
              style={{ borderColor: file ? '#A62B1F' : undefined }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange} />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <File className="w-5 h-5 text-[#A62B1F]" />
                  <span className="font-medium text-[#1E2A33]">{file.name}</span>
                  <span className="text-[#5C6670]">({fmtSize(file.size)})</span>
                </div>
              ) : (
                <div className="text-[#5C6670]">
                  <Upload className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Click to select a file</p>
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <Label className="mb-1.5 block">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
            />
          </div>

          {/* Type + Contact row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Contact / Account</Label>
              <Select
                value={contactId || '__none__'}
                onValueChange={(v) => setContactId(v === '__none__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {contacts.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.full_name}{c.company ? ` — ${c.company}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Projects */}
          {projects.length > 0 && (
            <div>
              <Label className="mb-1.5 block">Projects</Label>
              <div className="flex flex-wrap gap-2">
                {projects.map((p, idx) => {
                  const active = selectedProjectIds.includes(p._id);
                  return (
                    <button
                      key={p._id}
                      onClick={() => toggleProject(p._id)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
                      style={{
                        backgroundColor: active ? `${projectColor(p, idx)}22` : 'transparent',
                        color: active ? projectColor(p, idx) : '#5C6670',
                        borderColor: active ? `${projectColor(p, idx)}66` : '#D3CCBE',
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: projectColor(p, idx) }} />
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="mb-1.5 block">Notes <span className="text-[#5C6670] font-normal">(optional)</span></Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this document..."
              rows={2}
            />
          </div>

          {/* Signable flag */}
          <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={isSignable}
              onChange={e => setIsSignable(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#A62B1F]"
            />
            <div>
              <p className="text-sm font-medium text-[#1E2A33]">Available for e-signature</p>
              <p className="text-xs text-[#5C6670] mt-0.5">Show send for signature and template options on this document. Uncheck for reference-only files.</p>
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => { onClose(); reset(); }} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={uploading || !file || !title.trim()}
            style={{ backgroundColor: '#A62B1F' }}
            className="text-white hover:opacity-90"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// General file storage. E-signature (templates + in-progress signing) lives on
// its own page now — most documents never go through e-signature, and this
// page is likely to grow into a broader repository (social assets, etc.).

const NEW_KINDS = [
  { kind: 'docx', label: 'Word document', icon: FileText, placeholder: 'Proposal for…' },
  { kind: 'xlsx', label: 'Excel spreadsheet', icon: FileSpreadsheet, placeholder: 'Budget…' },
  { kind: 'pptx', label: 'PowerPoint deck', icon: Presentation, placeholder: 'Pitch for…' },
];

// ── New blank document (opens straight in the editor) ─────────────────────────

const NewDocDialog = ({ kind, projects, onClose }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);
  const info = NEW_KINDS.find((k) => k.kind === kind);
  useEffect(() => { setTitle(''); setProjectId(''); }, [kind]);
  const create = async () => {
    setBusy(true);
    try {
      const r = await editorAPI.create({ title: title.trim() || 'Untitled', kind, project_ids: projectId ? [projectId] : [],
        doc_type: kind === 'docx' ? 'other' : kind === 'xlsx' ? 'report' : 'proposal' });
      navigate(`/documents/${r.data._id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the document');
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New {info?.label.toLowerCase()}</DialogTitle>
          <DialogDescription>It opens in the editor right away and saves as you go.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input autoFocus value={title} placeholder={info?.placeholder} onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && create()} />
          </div>
          {projects.length > 0 && (
            <div>
              <Label>Project</Label>
              <Select value={projectId || 'none'} onValueChange={(v) => setProjectId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((p) => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="text-white" style={{ backgroundColor: '#A62B1F' }} onClick={create} disabled={busy}>
            {busy ? 'Creating…' : 'Create and open'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Documents() {
  const { projects } = useProject();
  const navigate = useNavigate();
  const mods = useModules();
  const editorOn = hasModule(mods, 'editor');
  const [newKind, setNewKind] = useState(null);
  const openInEditor = (doc) => navigate(`/documents/${doc._id}/edit${canEditInEditor(doc) ? '' : '?mode=view'}`);
  const t = useT();
  const [docs, setDocs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterProjectId, setFilterProjectId] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [signDoc, setSignDoc] = useState(null);
  const [templateDoc, setTemplateDoc] = useState(null);
  const [viewSubId, setViewSubId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const projectMap = useMemo(() =>
    Object.fromEntries(projects.map((p, i) => [p._id, { ...p, _idx: i }])), [projects]);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await documentsAPI.list();
      setDocs(res.data);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const res = await contactsAPI.list({ limit: 500 });
      setContacts(res.data?.contacts || res.data || []);
    } catch {}
  };

  // Only fetched to power the "View signed" shortcut on a document row.
  // Sending/monitoring signatures itself lives on the eSignature page.
  const loadSubmissions = async () => {
    try {
      const res = await esignAPI.listSubmissions();
      setSubmissions(res.data);
    } catch {}
  };

  useEffect(() => {
    loadDocs();
    loadContacts();
    loadSubmissions();
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await documentsAPI.delete(deleteId);
      setDocs((prev) => prev.filter((d) => d._id !== deleteId));
      toast.success('Document deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await documentsAPI.download(doc._id, doc.filename);
    } catch {
      toast.error('Download failed');
    }
  };

  // ── Filtering + grouping ─────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = docs.filter((d) => !d.is_template); // templates live on the eSignature page instead
    if (filterType) list = list.filter((d) => d.doc_type === filterType);
    if (filterProjectId === '__none__') list = list.filter((d) => !(d.project_ids || []).length);
    else if (filterProjectId) list = list.filter((d) => (d.project_ids || []).includes(filterProjectId));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (d) =>
          d.title?.toLowerCase().includes(q) ||
          d.filename?.toLowerCase().includes(q) ||
          d.account_name?.toLowerCase().includes(q) ||
          d.contact_name?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [docs, filterType, filterProjectId, search]);

  // Group by account_name (company), then "No account" at the end
  const grouped = useMemo(() => {
    const groups = {};
    for (const doc of filtered) {
      const key = doc.account_name?.trim() || '—';
      if (!groups[key]) groups[key] = [];
      groups[key].push(doc);
    }
    // Sort: named accounts first (alpha), then "—"
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '—') return 1;
      if (b === '—') return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const activeFilterCount = [filterType, filterProjectId, search.trim()].filter(Boolean).length;

  return (
    <Layout title="Documents">
      <div className="p-6 max-w-6xl mx-auto space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('documents.title')}
            </h1>
            <p className="text-sm text-[#5C6670] mt-0.5">
              {filtered.length} document{filtered.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {editorOn && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <FilePlus className="w-4 h-4" /> New
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {NEW_KINDS.map(({ kind, label, icon: Icon }) => (
                    <DropdownMenuItem key={kind} onClick={() => setNewKind(kind)}>
                      <Icon className="w-4 h-4 mr-2" /> {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              onClick={() => setUploadOpen(true)}
              className="gap-2 text-white"
              style={{ backgroundColor: '#A62B1F' }}
            >
              <Plus className="w-4 h-4" />
              {t('documents.upload')}
            </Button>
          </div>
        </div>
        <NewDocDialog kind={newKind} projects={projects} onClose={() => setNewKind(null)} />

        {/* Filter bar */}
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              {/* Search — full width on mobile */}
              <div className="relative w-full md:flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6670]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search documents..."
                  className="pl-8 h-9 w-full"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">

              {/* Doc type */}
              <Select value={filterType || 'all'} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 w-36">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Project */}
              {projects.length >= 1 && (
                <Select value={filterProjectId || 'all'} onValueChange={(v) => setFilterProjectId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9 w-40">
                    <SelectValue placeholder="All projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All projects</SelectItem>
                    <SelectItem value="__none__">No project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Clear */}
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 gap-1 text-[#5C6670] hover:text-[#1E2A33]"
                  onClick={() => { setSearch(''); setFilterType(''); setFilterProjectId(''); }}
                >
                  <X className="w-3.5 h-3.5" />
                  Clear ({activeFilterCount})
                </Button>
              )}
              </div>{/* end flex-wrap row */}
            </div>
          </CardContent>
        </Card>

        {/* Document list */}
        {loading ? (
          <div className="text-center py-16 text-[#5C6670]">{t('documents.loading')}</div>
        ) : grouped.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <File className="w-12 h-12 mx-auto mb-3 text-[#D3CCBE]" />
              <p className="text-[#1E2A33] font-medium mb-1">{t('documents.none')}</p>
              <p className="text-sm text-[#5C6670]">Upload your first document to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {grouped.map(([accountName, accountDocs]) => {
              const collapsed = collapsedGroups.has(accountName);
              return (
                <Card key={accountName} className="overflow-hidden">
                  {/* Group header */}
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FBF8F2] transition-colors"
                    onClick={() => toggleGroup(accountName)}
                  >
                    {collapsed
                      ? <ChevronRight className="w-4 h-4 text-[#5C6670] flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-[#5C6670] flex-shrink-0" />}
                    <span className="font-semibold text-[#1E2A33] text-sm">
                      {accountName === '—' ? 'No account' : accountName}
                    </span>
                    <span className="text-xs text-[#5C6670] bg-[#F1ECE3] rounded-full px-2 py-0.5">
                      {accountDocs.length}
                    </span>
                  </button>

                  {/* Rows */}
                  {!collapsed && (
                    <div className="border-t">
                      {accountDocs.map((doc, i) => {
                        const signedSub = (doc.esign_status === 'signed' || doc.esign_status === 'executed')
                          ? submissions.find(s => s.document_id === doc._id)
                          : null;
                        return (
                          <div
                            key={doc._id}
                            className="px-4 py-3 hover:bg-[#FAFBFC] transition-colors"
                            style={{ borderTop: i > 0 ? '1px solid #F1ECE3' : undefined }}
                          >
                            {/* Row 1: icon + title + meta */}
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                                style={{ backgroundColor: `${(docTypeMap[doc.doc_type] ? '#A62B1F' : '#9AA3AB')}15` }}>
                                {React.createElement(
                                  (docTypeMap[doc.doc_type] || docTypeMap.other).icon,
                                  { className: 'w-4 h-4', style: { color: '#A62B1F' } },
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-[#1E2A33] truncate">{doc.title}</p>
                                <p className="text-xs text-[#5C6670] mt-0.5 truncate">
                                  {fmtSize(doc.size)} · {safeFormat(doc.created_at, 'MMM d, yyyy')}
                                  {doc.contact_name && ` · ${doc.contact_name}`}
                                </p>
                              </div>
                              {/* Desktop-only: action buttons inline */}
                              <div className="hidden md:flex items-center gap-1 flex-shrink-0">
                                {signedSub && (
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600 hover:bg-green-50" title="View signed" onClick={() => setViewSubId(signedSub._id)}>
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                )}
                                {editorOn && canOpenInEditor(doc) && (
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-[#5C6670] hover:text-[#A62B1F] hover:bg-purple-50" title={canEditInEditor(doc) ? 'Open in editor' : 'Open in viewer'} onClick={() => openInEditor(doc)}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-[#5C6670] hover:text-[#A62B1F] hover:bg-purple-50" title="Preview" onClick={() => setPreviewDoc(doc)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {doc.is_signable !== false && <>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-[#A62B1F] hover:bg-purple-50" title="Send for Signature" onClick={() => setSignDoc(doc)}>
                                    <PenLine className="w-4 h-4" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className={`h-8 w-8 hover:bg-amber-50 ${doc.is_template ? 'text-amber-600' : 'text-[#5C6670] hover:text-amber-600'}`} title={doc.is_template ? 'Edit template' : 'Save as template'} onClick={() => setTemplateDoc(doc)}>
                                    <LayoutTemplate className="w-4 h-4" />
                                  </Button>
                                </>}
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-[#5C6670]" title="Download" onClick={() => handleDownload(doc)}>
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:bg-red-50" title="Delete" onClick={() => setDeleteId(doc._id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Row 2: badges + mobile actions */}
                            <div className="flex items-center gap-2 mt-2 ml-11 flex-wrap">
                              <DocTypeBadge type={doc.doc_type} />
                              {doc.esign_status && <ESignBadge status={doc.esign_status} />}
                              {doc.is_template && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Template</span>
                              )}
                              {/* Project chips — visible on all sizes here */}
                              <div className="hidden md:flex items-center gap-1.5 flex-wrap">
                                {(doc.project_ids || []).map((pid) => {
                                  const p = projectMap[pid];
                                  return p ? <ProjectChip key={pid} project={p} idx={p._idx} /> : null;
                                })}
                              </div>
                              {/* Mobile-only action buttons as compact row */}
                              <div className="flex md:hidden items-center gap-1 ml-auto">
                                {signedSub && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => setViewSubId(signedSub._id)}>
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {editorOn && canOpenInEditor(doc) && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5C6670]" title="Open in editor" onClick={() => openInEditor(doc)}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5C6670]" title="Preview" onClick={() => setPreviewDoc(doc)}>
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                {doc.is_signable !== false && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#A62B1F]" title="Send for Signature" onClick={() => setSignDoc(doc)}>
                                    <PenLine className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5C6670]" onClick={() => handleDownload(doc)}>
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => setDeleteId(doc._id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Send for signature */}
      {signDoc && (
        <PrepareDocumentModal
          doc={signDoc}
          mode="send"
          onClose={() => setSignDoc(null)}
          onSent={() => { loadDocs(); loadSubmissions(); }}
        />
      )}

      {/* Preview */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
          onDownload={handleDownload}
        />
      )}

      {/* Submission viewer */}
      {viewSubId && (
        <SubmissionViewerModal
          submissionId={viewSubId}
          onClose={() => setViewSubId(null)}
        />
      )}

      {/* Make / edit template */}
      {templateDoc && (
        <PrepareDocumentModal
          doc={templateDoc}
          mode="template"
          onClose={() => setTemplateDoc(null)}
          onSent={loadDocs}
        />
      )}

      {/* Upload dialog */}
      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        projects={projects}
        contacts={contacts}
        onUploaded={(newDoc) => setDocs((prev) => [newDoc, ...prev])}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => { if (!v) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the document and its file. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('documents.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
